export const DRAWFLOW_LAYOUT_HORIZONTAL_GAP = 96;
export const DRAWFLOW_LAYOUT_VERTICAL_GAP = 48;
export const DRAWFLOW_LAYOUT_COMPONENT_GAP = 96;
export const DRAWFLOW_LAYOUT_ORIGIN = 48;

const DEFAULT_NODE_WIDTH = 250;
const DEFAULT_NODE_HEIGHT = 80;
const BARYCENTER_SWEEPS = 4;

const compareIds = (left, right) => String(left).localeCompare(String(right), "en", {
  numeric: true,
});

const compareOutputKeys = (left, right) => {
  const leftNumber = Number(String(left).match(/\d+$/)?.[0]);
  const rightNumber = Number(String(right).match(/\d+$/)?.[0]);

  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return compareIds(left, right);
};

const normalizeSize = size => ({
  width: Number.isFinite(size?.width) && size.width > 0
    ? size.width
    : DEFAULT_NODE_WIDTH,
  height: Number.isFinite(size?.height) && size.height > 0
    ? size.height
    : DEFAULT_NODE_HEIGHT,
});

const buildGraph = nodes => {
  const ids = Object.keys(nodes ?? {}).sort(compareIds);
  const idSet = new Set(ids);
  const successors = new Map(ids.map(id => [id, []]));
  const predecessors = new Map(ids.map(id => [id, []]));

  ids.forEach(id => {
    const targets = new Set();
    const outputs = nodes[id]?.outputs ?? {};
    Object.keys(outputs).sort(compareOutputKeys).forEach(outputKey => {
      (outputs[outputKey]?.connections ?? []).forEach(connection => {
        const targetId = String(connection?.node ?? "");
        if (!idSet.has(targetId) || targets.has(targetId)) return;

        targets.add(targetId);
        successors.get(id).push(targetId);
        predecessors.get(targetId).push(id);
      });
    });
  });

  return { ids, predecessors, successors };
};

const createTraversalOrder = ({ ids, predecessors, successors }) => {
  const roots = ids.filter(id => predecessors.get(id).length === 0);
  const queue = [...roots];
  const visited = new Set(queue);
  const order = [];

  const visitQueue = () => {
    while (queue.length) {
      const id = queue.shift();
      order.push(id);
      successors.get(id).forEach(targetId => {
        if (visited.has(targetId)) return;
        visited.add(targetId);
        queue.push(targetId);
      });
    }
  };

  visitQueue();
  ids.forEach(id => {
    if (visited.has(id)) return;
    visited.add(id);
    queue.push(id);
    visitQueue();
  });

  return new Map(order.map((id, index) => [id, index]));
};

const findWeakComponents = (graph, traversalOrder) => {
  const remaining = new Set(graph.ids);
  const components = [];

  graph.ids.forEach(startId => {
    if (!remaining.has(startId)) return;

    const component = [];
    const queue = [startId];
    remaining.delete(startId);

    while (queue.length) {
      const id = queue.shift();
      component.push(id);
      const neighbours = [
        ...graph.successors.get(id),
        ...graph.predecessors.get(id),
      ];
      neighbours.sort((left, right) => traversalOrder.get(left) - traversalOrder.get(right));
      neighbours.forEach(neighbourId => {
        if (!remaining.has(neighbourId)) return;
        remaining.delete(neighbourId);
        queue.push(neighbourId);
      });
    }

    component.sort((left, right) => traversalOrder.get(left) - traversalOrder.get(right));
    components.push(component);
  });

  return components.sort((left, right) => (
    traversalOrder.get(left[0]) - traversalOrder.get(right[0])
  ));
};

const findStrongComponents = (nodeIds, successors, traversalOrder) => {
  const allowedIds = new Set(nodeIds);
  const indices = new Map();
  const lowLinks = new Map();
  const stack = [];
  const onStack = new Set();
  const components = [];
  let nextIndex = 0;

  const connect = id => {
    indices.set(id, nextIndex);
    lowLinks.set(id, nextIndex);
    nextIndex += 1;
    stack.push(id);
    onStack.add(id);

    successors.get(id).forEach(targetId => {
      if (!allowedIds.has(targetId)) return;
      if (!indices.has(targetId)) {
        connect(targetId);
        lowLinks.set(id, Math.min(lowLinks.get(id), lowLinks.get(targetId)));
      } else if (onStack.has(targetId)) {
        lowLinks.set(id, Math.min(lowLinks.get(id), indices.get(targetId)));
      }
    });

    if (lowLinks.get(id) !== indices.get(id)) return;

    const component = [];
    let currentId;
    do {
      currentId = stack.pop();
      onStack.delete(currentId);
      component.push(currentId);
    } while (currentId !== id);
    component.sort((left, right) => traversalOrder.get(left) - traversalOrder.get(right));
    components.push(component);
  };

  nodeIds.forEach(id => {
    if (!indices.has(id)) connect(id);
  });

  return components;
};

const assignRanks = (nodeIds, graph, traversalOrder) => {
  const components = findStrongComponents(nodeIds, graph.successors, traversalOrder);
  const componentByNode = new Map();
  components.forEach((component, index) => {
    component.forEach(id => componentByNode.set(id, index));
  });

  const componentSuccessors = components.map(() => new Set());
  const componentIndegrees = components.map(() => 0);
  nodeIds.forEach(id => {
    graph.successors.get(id).forEach(targetId => {
      const sourceComponent = componentByNode.get(id);
      const targetComponent = componentByNode.get(targetId);
      if (sourceComponent === targetComponent
        || componentSuccessors[sourceComponent].has(targetComponent)) return;
      componentSuccessors[sourceComponent].add(targetComponent);
      componentIndegrees[targetComponent] += 1;
    });
  });

  const componentOrder = component => Math.min(
    ...components[component].map(id => traversalOrder.get(id))
  );
  const queue = components
    .map((component, index) => index)
    .filter(index => componentIndegrees[index] === 0)
    .sort((left, right) => componentOrder(left) - componentOrder(right));
  const ranks = components.map(() => 0);

  while (queue.length) {
    const component = queue.shift();
    [...componentSuccessors[component]]
      .sort((left, right) => componentOrder(left) - componentOrder(right))
      .forEach(targetComponent => {
        ranks[targetComponent] = Math.max(ranks[targetComponent], ranks[component] + 1);
        componentIndegrees[targetComponent] -= 1;
        if (componentIndegrees[targetComponent] === 0) {
          queue.push(targetComponent);
          queue.sort((left, right) => componentOrder(left) - componentOrder(right));
        }
      });
  }

  return new Map(nodeIds.map(id => [id, ranks[componentByNode.get(id)]]));
};

const orderRanks = (nodeIds, ranks, graph, traversalOrder) => {
  const maxRank = Math.max(...ranks.values());
  const nodesByRank = Array.from({ length: maxRank + 1 }, () => []);
  nodeIds.forEach(id => nodesByRank[ranks.get(id)].push(id));
  nodesByRank.forEach(ids => ids.sort(
    (left, right) => traversalOrder.get(left) - traversalOrder.get(right)
  ));

  const createPositionMap = () => new Map(nodesByRank.flatMap(ids => (
    ids.map((id, index) => [id, index])
  )));
  const sortByNeighbours = (rank, neighbours, neighbourPredicate) => {
    const positions = createPositionMap();
    nodesByRank[rank].sort((left, right) => {
      const score = id => {
        const relatedPositions = neighbours.get(id)
          .filter(neighbourId => neighbourPredicate(ranks.get(neighbourId), rank))
          .map(neighbourId => positions.get(neighbourId));
        if (!relatedPositions.length) return null;
        return relatedPositions.reduce((total, position) => total + position, 0)
          / relatedPositions.length;
      };
      const leftScore = score(left);
      const rightScore = score(right);
      if (leftScore !== null && rightScore !== null && leftScore !== rightScore) {
        return leftScore - rightScore;
      }
      if (leftScore === null && rightScore !== null) return 1;
      if (leftScore !== null && rightScore === null) return -1;
      return traversalOrder.get(left) - traversalOrder.get(right);
    });
  };

  for (let sweep = 0; sweep < BARYCENTER_SWEEPS; sweep += 1) {
    for (let rank = 1; rank <= maxRank; rank += 1) {
      sortByNeighbours(rank, graph.predecessors, neighbourRank => neighbourRank < rank);
    }
    for (let rank = maxRank - 1; rank >= 0; rank -= 1) {
      sortByNeighbours(rank, graph.successors, neighbourRank => neighbourRank > rank);
    }
  }

  return nodesByRank;
};

const roundCoordinate = coordinate => Number(coordinate.toFixed(2));

/**
 * Computes a deterministic left-to-right layout without mutating Drawflow data.
 */
export const computeAutomaticLayout = (
  nodes,
  dimensions = {},
  {
    horizontalGap = DRAWFLOW_LAYOUT_HORIZONTAL_GAP,
    verticalGap = DRAWFLOW_LAYOUT_VERTICAL_GAP,
    componentGap = DRAWFLOW_LAYOUT_COMPONENT_GAP,
    origin = DRAWFLOW_LAYOUT_ORIGIN,
  } = {}
) => {
  const graph = buildGraph(nodes);
  if (!graph.ids.length) return { positions: {}, width: 0, height: 0 };

  const traversalOrder = createTraversalOrder(graph);
  const components = findWeakComponents(graph, traversalOrder);
  const positions = {};
  let componentTop = origin;
  let layoutWidth = 0;

  components.forEach(component => {
    const ranks = assignRanks(component, graph, traversalOrder);
    const nodesByRank = orderRanks(component, ranks, graph, traversalOrder);
    const columnWidths = nodesByRank.map(ids => Math.max(
      ...ids.map(id => normalizeSize(dimensions[id]).width)
    ));
    const columnHeights = nodesByRank.map(ids => (
      ids.reduce((height, id) => height + normalizeSize(dimensions[id]).height, 0)
      + Math.max(0, ids.length - 1) * verticalGap
    ));
    const componentHeight = Math.max(...columnHeights);
    let columnLeft = origin;

    nodesByRank.forEach((ids, rank) => {
      let nodeTop = componentTop + (componentHeight - columnHeights[rank]) / 2;
      ids.forEach(id => {
        positions[id] = {
          x: roundCoordinate(columnLeft),
          y: roundCoordinate(nodeTop),
        };
        nodeTop += normalizeSize(dimensions[id]).height + verticalGap;
      });
      columnLeft += columnWidths[rank] + horizontalGap;
    });

    layoutWidth = Math.max(layoutWidth, columnLeft - horizontalGap + origin);
    componentTop += componentHeight + componentGap;
  });

  return {
    positions,
    width: roundCoordinate(layoutWidth),
    height: roundCoordinate(componentTop - componentGap + origin),
  };
};
